import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    appSlug: String,
    projectId: String,
    triggerType: String
  }

  declare appSlugValue: string
  declare projectIdValue: string
  declare triggerTypeValue: string

  private hideTimer: ReturnType<typeof setTimeout> | null = null
  private clickOpened = false

  private _enter!: () => void
  private _leave!: (event: MouseEvent) => void
  private _click!: (event: Event) => void
  private _onToggle!: (event: Event) => void
  private _boundCancelHide!: () => void

  get triggerElement(): HTMLElement | null {
    return this.element.querySelector('[data-menu-app-trigger]')
  }

  get popupElement(): HTMLElement | null {
    return this.element.querySelector('[data-menu-app-popup]')
  }

  connect() {
    this.hideTimer = null
    this.clickOpened = false

    this._enter = this.onEnter.bind(this)
    this._leave = this.onLeave.bind(this)
    this._click = this.onClick.bind(this)
    this._onToggle = this.onToggle.bind(this)
    this._boundCancelHide = this.cancelHide.bind(this)

    const trigger = this.triggerElement
    if (trigger) {
      trigger.addEventListener('mouseenter', this._enter)
      trigger.addEventListener('mouseleave', this._leave)
      if (this.triggerTypeValue === 'button') {
        trigger.addEventListener('click', this._click)
      }
    }

    const popup = this.popupElement
    if (popup) {
      popup.addEventListener('mouseenter', this._boundCancelHide)
      popup.addEventListener('mouseleave', this._leave)
      popup.addEventListener('toggle', this._onToggle)
    }
  }

  disconnect() {
    const trigger = this.triggerElement
    if (trigger) {
      trigger.removeEventListener('mouseenter', this._enter)
      trigger.removeEventListener('mouseleave', this._leave)
      if (this.triggerTypeValue === 'button') trigger.removeEventListener('click', this._click)
    }

    const popup = this.popupElement
    if (popup) {
      popup.removeEventListener('mouseenter', this._boundCancelHide)
      popup.removeEventListener('mouseleave', this._leave)
      popup.removeEventListener('toggle', this._onToggle)
    }
  }

  onEnter() {
    if (this.clickOpened) return
    this.showPopup()
  }

  onLeave(event: MouseEvent) {
    if (this.clickOpened) return
    if (event.relatedTarget && this.element.contains(event.relatedTarget as Node)) return
    this.hideTimer = setTimeout(() => this.hidePopup(), 100)
  }

  cancelHide() {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer)
      this.hideTimer = null
    }
  }

  onClick(event: Event) {
    if (this.triggerTypeValue !== 'button') return
    event.preventDefault()
    const popup = this.popupElement
    if (!popup) return

    if (this.clickOpened) {
      this.hidePopup()
      this.clickOpened = false
    } else {
      this.showPopup()
      this.clickOpened = true
    }
  }

  onToggle(event: Event) {
    const toggleEvent = event as Event & { newState: 'open' | 'closed' }
    if (toggleEvent.newState === 'closed') {
      setTimeout(() => { this.clickOpened = false }, 0)
    }
  }

  showPopup() {
    this.cancelHide()
    if (this.popupElement && !this.popupElement.matches(':popover-open')) {
      this.popupElement.showPopover()
    }
  }

  hidePopup() {
    if (this.popupElement && this.popupElement.matches(':popover-open')) {
      this.popupElement.hidePopover()
    }
  }
}
